import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import NexWordmark from "@/components/NexWordmark";
import { Loader2, CheckCircle2, ExternalLink, Send, Sparkles, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const embedUrl = (url) => {
  if (!url) return null;
  // Convert Loom share links to embed
  const loomMatch = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  // Convert YouTube watch links to embed
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  return url;
};

const MicroLandingPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [busyStep, setBusyStep] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/c/${token}`);
      setData(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "This link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const completeStep = async (i, payload = null) => {
    setBusyStep(i);
    try {
      const { data: res } = await axios.post(`${API}/public/c/${token}/complete-step`, { step_index: i, payload });
      toast.success(res.all_completed ? "🎉 All steps complete — nice work!" : "Step completed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusyStep(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <Loader2 className="animate-spin text-nex-gold" size={24} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex flex-col items-center justify-center px-6 text-center">
        <NexWordmark size="text-2xl" />
        <div className="mono-label mt-4 mb-2 !text-red-300">// LINK ERROR</div>
        <p className="text-nex-chrome max-w-md">{error}</p>
        <a href="https://nexengines.com" className="tick-btn-secondary text-xs mt-6">Back to nexengines.com</a>
      </div>
    );
  }

  const firstName = data.contact?.first_name || "there";
  const allDone = data.current_step_index >= data.steps.length;
  const video = embedUrl(data.video_url);

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="micro-landing-page">
      <div className="max-w-xl mx-auto px-4 md:px-6 py-8 md:py-14">
        <div className="text-center mb-8">
          <NexWordmark size="text-xl md:text-2xl" />
          <div className="mono-body text-[10px] tracking-[0.3em] uppercase text-nex-gold mt-2">
            // {data.kind?.replace(/_/g, " ")}
          </div>
        </div>

        <div className="card-industrial corner-tick p-6 md:p-8" data-testid="micro-content">
          <div className="mono-label mb-2">// FOR</div>
          <div className="text-nex-silver text-sm mb-4">{firstName}{data.contact?.business_name ? ` · ${data.contact.business_name}` : ""}</div>

          <h1 className="serif-head text-3xl md:text-4xl mb-2">{data.title}</h1>
          {data.subtitle && <p className="text-nex-chrome mb-6">{data.subtitle}</p>}

          {video && (
            <div className="mb-6 border border-nex-border overflow-hidden aspect-video bg-black" data-testid="video-embed">
              <iframe
                src={video}
                title="NEX greeting"
                allowFullScreen
                allow="autoplay; encrypted-media; fullscreen"
                className="w-full h-full"
              />
            </div>
          )}

          {data.body_md && (
            <div className="text-nex-chrome text-sm whitespace-pre-wrap mb-6">{data.body_md}</div>
          )}

          {/* STEPS */}
          <div className="space-y-3" data-testid="steps-list">
            {data.steps.map((s, i) => {
              const done = !!s.completed_at;
              const active = !done && i === data.current_step_index;
              const locked = !done && i > data.current_step_index;

              return (
                <div
                  key={i}
                  className={`border p-4 md:p-5 transition ${
                    done ? "border-emerald-500/40 bg-emerald-500/5" :
                    active ? "border-nex-gold bg-nex-gold/5" :
                    "border-nex-border opacity-60"
                  }`}
                  data-testid={`step-${i}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-6 h-6 flex items-center justify-center border ${done ? "border-emerald-500 bg-emerald-500 text-black" : active ? "border-nex-gold text-nex-gold" : "border-nex-border text-nex-muted"}`}>
                      {done ? <CheckCircle2 size={13} /> : <span className="mono-body text-[10px]">{i + 1}</span>}
                    </div>
                    <div className="text-nex-silver font-medium">{s.title}</div>
                  </div>

                  {active && (
                    <div className="mt-3">
                      {s.kind === "confirm" && (
                        <button
                          onClick={() => completeStep(i)}
                          disabled={busyStep === i}
                          className="tick-btn-primary w-full justify-center"
                          data-testid={`step-${i}-confirm`}
                        >
                          {busyStep === i ? <Loader2 size={14} className="animate-spin" /> : <>Mark as done <CheckCircle2 size={14} /></>}
                        </button>
                      )}
                      {s.kind === "link" && s.href && (
                        <>
                          <a
                            href={s.href} target="_blank" rel="noopener noreferrer"
                            className="tick-btn-primary w-full justify-center mb-2"
                            data-testid={`step-${i}-link`}
                          >
                            Open <ExternalLink size={14} />
                          </a>
                          <button
                            onClick={() => completeStep(i, { visited: s.href })}
                            disabled={busyStep === i}
                            className="mono-body text-[11px] uppercase tracking-[0.2em] text-nex-gold w-full text-center"
                            data-testid={`step-${i}-linkdone`}
                          >
                            {busyStep === i ? "…" : "I'm done — advance"}
                          </button>
                        </>
                      )}
                      {s.kind === "form" && (
                        <div className="space-y-2">
                          {s.prompt && <div className="text-nex-chrome text-sm">{s.prompt}</div>}
                          <input
                            type="text"
                            value={formValues[i] || ""}
                            onChange={e => setFormValues(prev => ({ ...prev, [i]: e.target.value }))}
                            className="input-industrial w-full"
                            placeholder="Enter here…"
                            data-testid={`step-${i}-input`}
                          />
                          <button
                            onClick={() => completeStep(i, { value: formValues[i] || "" })}
                            disabled={busyStep === i || !(formValues[i] || "").trim()}
                            className="tick-btn-primary w-full justify-center"
                            data-testid={`step-${i}-submit`}
                          >
                            {busyStep === i ? <Loader2 size={14} className="animate-spin" /> : <>Submit <Send size={14} /></>}
                          </button>
                        </div>
                      )}
                      {s.kind === "checkout" && (
                        <>
                          <div className="text-nex-chrome text-sm mb-2">Click below to complete payment. We&apos;ll advance you automatically when the payment clears.</div>
                          <a
                            href="https://cal.com/nexengines/kick-off"
                            target="_blank" rel="noopener noreferrer"
                            className="tick-btn-primary w-full justify-center mb-2"
                            data-testid={`step-${i}-checkout`}
                          >
                            Book kickoff & pay <PlayCircle size={14} />
                          </a>
                          <button
                            onClick={() => completeStep(i)}
                            disabled={busyStep === i}
                            className="mono-body text-[11px] uppercase tracking-[0.2em] text-nex-gold w-full text-center"
                          >
                            I already paid — advance
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {done && s.payload?.value && (
                    <div className="mono-body text-[10px] text-nex-muted mt-2">Submitted: {s.payload.value}</div>
                  )}
                  {locked && <div className="mono-body text-[10px] text-nex-muted">Complete the step above to unlock</div>}
                </div>
              );
            })}
          </div>

          {allDone && (
            <div className="mt-6 border border-emerald-500/40 bg-emerald-500/5 p-4 text-center" data-testid="all-done">
              <Sparkles className="text-emerald-300 mx-auto mb-2" size={20} />
              <div className="text-emerald-300 font-medium mb-1">You&apos;re all set.</div>
              <div className="text-nex-chrome text-sm">Your NEX team will reach out with next steps.</div>
            </div>
          )}
        </div>

        <div className="mono-body text-[10px] text-nex-muted text-center mt-6">
          Personal link · secure · nexengines.com
        </div>
      </div>
    </div>
  );
};

export default MicroLandingPage;
